import React, { useState, useEffect } from 'react';
import costService from '../services/costService';
import LoadingSpinner from './LoadingSpinner';

const CostStats = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('');

  const CATEGORIES = {
    'MATERIA_PRIMA': 'Matéria-prima',
    'FERRAGENS': 'Ferragens',
    'INSUMOS': 'Insumos',
    'SERVICOS': 'Serviços',
    'LOGISTICA': 'Logística',
    'SOFTWARE': 'Software',
    'OUTROS': 'Outros'
  };

  useEffect(() => {
    loadStats();
  }, [selectedCategory]);

  const loadStats = async () => {
    try {
      setLoading(true);
      const params = selectedCategory ? { category: selectedCategory } : {};
      const response = await costService.getCostStats(params);
      setStats(response.data);
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  if (loading) return <LoadingSpinner />;

  if (!stats) return null;

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-800">📊 Estatísticas de Custos</h1>
        <div>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Todas as categorias</option>
            {Object.entries(CATEGORIES).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Cards de Estatísticas Gerais */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <span className="text-2xl">📦</span>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total de Custos</p>
              <p className="text-2xl font-bold text-gray-900">{stats.stats.total_costs}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <span className="text-2xl">🏷️</span>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Categorias</p>
              <p className="text-2xl font-bold text-gray-900">{stats.stats.total_categories}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <span className="text-2xl">🏪</span>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Fornecedores</p>
              <p className="text-2xl font-bold text-gray-900">{stats.stats.total_suppliers}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <span className="text-2xl">💰</span>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Valor Total</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(stats.stats.total_cost_value)}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Top Fornecedores */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-xl font-bold text-gray-800 mb-4">🏆 Top Fornecedores</h2>
          <div className="space-y-4">
            {stats.top_suppliers.slice(0, 5).map((supplier, index) => (
              <div key={supplier.main_supplier} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                    <span className="text-sm font-bold text-blue-600">#{index + 1}</span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{supplier.main_supplier}</p>
                    <p className="text-sm text-gray-500">{supplier.items_count} itens</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-medium text-gray-900">{formatCurrency(supplier.total_value)}</p>
                  <p className="text-sm text-gray-500">Valor total</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Custos por Categoria */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-xl font-bold text-gray-800 mb-4">📊 Custos por Categoria</h2>
          <div className="space-y-4">
            {stats.costs_by_category.map((category) => (
              <div key={category.category} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">{category.category_label}</p>
                  <p className="text-sm text-gray-500">
                    {category.items_count} itens • Média: {formatCurrency(category.average_cost)}
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-medium text-gray-900">{formatCurrency(category.total_value)}</p>
                  <p className="text-sm text-gray-500">Total</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Gráfico de Barras Simples - Custos por Categoria */}
      <div className="mt-8 bg-white p-6 rounded-lg shadow">
        <h2 className="text-xl font-bold text-gray-800 mb-4">📈 Distribuição de Custos por Categoria</h2>
        <div className="space-y-3">
          {stats.costs_by_category.map((category) => {
            const maxValue = Math.max(...stats.costs_by_category.map(c => c.total_value));
            const percentage = (category.total_value / maxValue) * 100;
            
            return (
              <div key={category.category} className="flex items-center">
                <div className="w-32 text-sm font-medium text-gray-700">
                  {category.category_label}
                </div>
                <div className="flex-1 mx-4">
                  <div className="w-full bg-gray-200 rounded-full h-6">
                    <div
                      className="bg-blue-600 h-6 rounded-full transition-all duration-300"
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                </div>
                <div className="w-24 text-sm text-gray-900 text-right">
                  {formatCurrency(category.total_value)}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Resumo de Preços */}
      <div className="mt-8 bg-white p-6 rounded-lg shadow">
        <h2 className="text-xl font-bold text-gray-800 mb-4">💡 Insights</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 bg-blue-50 rounded-lg">
            <h3 className="font-medium text-blue-900 mb-2">💰 Custo Médio</h3>
            <p className="text-2xl font-bold text-blue-600">
              {formatCurrency(stats.stats.average_cost)}
            </p>
            <p className="text-sm text-blue-700">Por item cadastrado</p>
          </div>
          
          <div className="p-4 bg-green-50 rounded-lg">
            <h3 className="font-medium text-green-900 mb-2">🏆 Maior Fornecedor</h3>
            <p className="text-lg font-bold text-green-600">
              {stats.top_suppliers[0]?.main_supplier || 'N/A'}
            </p>
            <p className="text-sm text-green-700">
              {stats.top_suppliers[0]?.items_count || 0} itens
            </p>
          </div>
          
          <div className="p-4 bg-purple-50 rounded-lg">
            <h3 className="font-medium text-purple-900 mb-2">📊 Categoria Principal</h3>
            <p className="text-lg font-bold text-purple-600">
              {stats.costs_by_category[0]?.category_label || 'N/A'}
            </p>
            <p className="text-sm text-purple-700">
              {formatCurrency(stats.costs_by_category[0]?.total_value || 0)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CostStats;
